import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuthStore } from '@/auth/auth-store';

/**
 * "Ongoing Session" modal — shown when `GetUserDataToken` /
 * `CheckUserDataToken` returns 409 indicating an existing session for the
 * same `(UserId, AppType)` in `User_Tokens`. User chooses to take over
 * (clears the conflicting session + retries auth) or cancel (logs out
 * locally and stays on /login).
 *
 * Mirrors Flutter `showActiveSessionPopup` (`session_helper.dart:284-320`):
 *   - Title:   "Ongoing Session"  (l10n key `ongoingSession`)
 *   - Body:    "There is an active session going on with this user,
 *              would you like them to logout and continue?"
 *              (l10n key `thereIsAnActiveSessionGoingOnWithThisUser`)
 *   - Yes CTA: triggers `LogoutEvent(state.response)` → backend clears
 *              the User_Tokens row → re-fires `DoLoginEvent`.
 *   - No CTA:  dismisses + leaves user on the login screen.
 *
 * Mounted once at app root (App.tsx). Subscribes to
 * `useAuthStore(s => s.activeSessionConflict)`; opens iff non-null.
 * The dialog is non-dismissable via outside-click or escape — the only
 * exits are the Yes/No buttons (matches Flutter `confirmDialog` which
 * blocks barrier-tap dismiss).
 *
 * Spec ref: PROD-946. Storybook story alongside in
 * `active-session-conflict-dialog.stories.tsx`.
 */
export function ActiveSessionConflictDialog() {
  const activeSessionConflict = useAuthStore((s) => s.activeSessionConflict);
  const confirmTakeover = useAuthStore((s) => s.confirmTakeover);
  const cancelTakeover = useAuthStore((s) => s.cancelTakeover);

  // Local in-flight state — covers the UpdateDeviceDetails + retry round
  // trip after Yes / Cancel is clicked. While in-flight both buttons
  // disable so a double-click can't double-submit.
  const [isInFlight, setIsInFlight] = useState(false);

  const open = activeSessionConflict !== null;

  async function onConfirm() {
    if (isInFlight) return;
    setIsInFlight(true);
    try {
      await confirmTakeover();
    } finally {
      setIsInFlight(false);
    }
  }

  async function onCancel() {
    if (isInFlight) return;
    setIsInFlight(true);
    try {
      await cancelTakeover();
    } finally {
      setIsInFlight(false);
    }
  }

  return (
    <AlertDialog open={open}>
      <AlertDialogContent className="sm:max-w-[var(--dialog-width-confirm)]">
        <AlertDialogHeader>
          <AlertDialogTitle>Ongoing Session</AlertDialogTitle>
          <AlertDialogDescription className="[font:var(--type-body)] text-[var(--ink-secondary)]">
            There is an active session going on with this user, would you like
            them to logout and continue?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={(e) => {
              // Prevent base-ui's default close — our cancelTakeover
              // handles the state flip + logout.
              e.preventDefault();
              void onCancel();
            }}
            disabled={isInFlight}
          >
            No
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              void onConfirm();
            }}
            disabled={isInFlight}
          >
            {isInFlight ? 'Logging out other session…' : 'Yes'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default ActiveSessionConflictDialog;
